import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface PersonnelFolderCardProps {
  name: string;
  email: string;
  credentialHint?: string;
  details?: string[];
  onClick?: () => void;
}

const passwordLabel = (credentialHint?: string) =>
  credentialHint?.replace(/^Password:\s*/i, '') || 'Not set';

export default function PersonnelFolderCard({ name, email, credentialHint, details, onClick }: PersonnelFolderCardProps) {
  const detailLines = details ?? [
    `Login: ${email}`,
    `Password: ${passwordLabel(credentialHint)}`,
  ];

  const card = (
    <Paper
      variant="outlined"
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: 172,
        p: 2,
        pt: 7.25,
        borderRadius: '18px',
        overflow: 'hidden',
        textAlign: 'left',
        borderColor: 'rgba(255,255,255,0.16)',
        color: '#fffaf1',
        background: `
          radial-gradient(circle at 24% 10%, rgba(255,255,255,0.14), transparent 34%),
          linear-gradient(180deg, #2d2d33 0%, #222229 48%, #18181e 100%)
        `,
        boxShadow: '0 22px 48px rgba(18,18,18,0.22), inset 0 1px 0 rgba(255,255,255,0.12)',
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: 46,
          background: `
            linear-gradient(135deg, transparent 0 28px, #2d2d33 29px 112px, transparent 113px),
            linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.02))
          `,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.14)',
        },
        '&:after': {
          content: '""',
          position: 'absolute',
          inset: '1px',
          borderRadius: '17px',
          pointerEvents: 'none',
          background: `
            linear-gradient(145deg, rgba(255,255,255,0.16), transparent 32%),
            linear-gradient(0deg, rgba(0,0,0,0.22), transparent 44%)
          `,
        },
      }}
    >
      <Stack spacing={1} sx={{ position: 'relative', zIndex: 1 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 900, lineHeight: 1.15, color: '#fffdf7' }}>
          {name}
        </Typography>
        {detailLines.map((line) => (
          <Typography
            key={line}
            sx={{ fontSize: 12.5, color: '#f5ead7', lineHeight: 1.35, fontWeight: 700, overflowWrap: 'anywhere' }}
          >
            {line}
          </Typography>
        ))}
      </Stack>
    </Paper>
  );

  if (!onClick) return card;

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%',
        display: 'block',
        borderRadius: 2,
        textAlign: 'left',
        '&:hover .MuiPaper-root, &:focus-visible .MuiPaper-root': {
          transform: 'translateY(-3px)',
          borderColor: 'rgba(255,255,255,0.30)',
          boxShadow: '0 28px 58px rgba(18,18,18,0.28), inset 0 1px 0 rgba(255,255,255,0.18)',
        },
        '&:active .MuiPaper-root': {
          transform: 'translateY(0) scale(0.99)',
          boxShadow: '0 14px 34px rgba(20,20,20,0.18), inset 0 1px 0 rgba(255,255,255,0.10)',
        },
      }}
    >
      {card}
    </ButtonBase>
  );
}
